import { Localized, useLocalization } from "@fluent/react";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Link,
    Stack,
    Typography,
} from "@mui/material";
import { LoadingContainer } from "../../../app/LoadingContainer";
import { CloseButton } from "../../../app/components/CloseButton";
import { UploadedFileDto } from "../supportIntegrationSlice";
import { downloadAttachment } from "../helpers/bugAttachmentHelpers";
import { BugBusinessCriticalChip } from "./BugBusinessCriticalChip";
import { CompanyBugPriorityChip } from "./CompanyBugPriorityChip";
import { CompanyBugStatusChip } from "./CompanyBugStatusChip";

export type BugDetailsData = {
    id: number;
    title: string;
    priority: string;
    isBusinessCritical: boolean;
    status?: string | null;
    createDate: string;
    reportedBy?: string | null;
    description: string;
    screenLink?: string | null;
    supportComment?: string | null;
    uploadedFiles: UploadedFileDto[];
};

type Props = {
    open: boolean;
    bug: BugDetailsData | null;
    loading?: boolean;
    error?: boolean;
    showReportedBy?: boolean;
    onClose: () => void;
    onExited: () => void;
};

const Detail = ({ label, value }: { label: string; value?: string | null }) => value ? (
    <Box>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
            {value}
        </Typography>
    </Box>
) : null;

const ScreenLinkDetail = ({ value }: { value?: string | null }) => {
    const { l10n } = useLocalization();
    let safeUrl: string | null = null;
    try {
        const parsedUrl = new URL(value ?? "");
        if (parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:") {
            safeUrl = parsedUrl.toString();
        }
    } catch {
        safeUrl = null;
    }

    return (
        <Box>
            <Typography variant="caption" color="text.secondary">
                {l10n.getString("bug-report-screen-link", null, "Screen link")}
            </Typography>
            {!value
                ? <Typography variant="body2" color="text.secondary">{l10n.getString("bug-report-screen-link-none", null, "None")}</Typography>
                : safeUrl
                ?  <Link href={safeUrl} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ display: "block", overflowWrap: "anywhere" }}>{value}</Link>
                : <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>{value}</Typography>}
        </Box>
    );
};

export const BugDetailsDialog = ({ open, bug, loading, error, showReportedBy, onClose, onExited }: Props) => {
    const { l10n } = useLocalization();

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="md"
            TransitionProps={{ onExited }}
        >
            <DialogTitle>
                <Localized id="bug-report-list-details">Bug report details</Localized>
            </DialogTitle>
            <DialogContent>
                {loading && (
                    <LoadingContainer />
                )}
                {error && (
                    <Alert severity="error">
                        <Localized id="company-bugs-details-load-error">Bug report details could not be loaded.</Localized>
                    </Alert>
                )}
                {bug && (
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Chip label={`ID: ${bug.id}`} size="small" />
                            <Chip label={new Date(bug.createDate).toLocaleString()} size="small" variant="outlined" />
                            <BugBusinessCriticalChip isBusinessCritical={bug.isBusinessCritical} showPrefix />
                            <CompanyBugPriorityChip priority={bug.priority} showPrefix />
                            <CompanyBugStatusChip status={bug.status} showPrefix />
                        </Stack>
                        {showReportedBy && (
                            <Detail label={l10n.getString("company-bugs-reported-by", null, "Reported by")} value={bug.reportedBy} />
                        )}
                        <Detail label={l10n.getString("bug-report-subject", null, "Subject")} value={bug.title} />
                        <Detail label={l10n.getString("bug-report-description", null, "Description")} value={bug.description} />
                        <ScreenLinkDetail value={bug.screenLink} />
                        <Detail label={l10n.getString("bug-report-list-support-comment", null, "Support comment")} value={bug.supportComment} />
                        <Divider />
                        <Typography variant="subtitle2"><Localized id="bug-report-list-attachments">Attachments</Localized></Typography>
                        {bug.uploadedFiles?.length ? (
                            <Stack spacing={1}>
                                {bug.uploadedFiles.map(attachment => (
                                    <Box key={attachment.guid} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5 }}>
                                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                            <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>{attachment.fileName}</Typography>
                                            <Button size="small" startIcon={<DownloadOutlinedIcon />} onClick={() => downloadAttachment(attachment)} disabled={!attachment.fileData}>
                                                <Localized id="download">Download</Localized>
                                            </Button>
                                        </Stack>
                                        {attachment.fileData && attachment.contentType?.startsWith("image/") && (
                                            <Box component="img" src={`data:${attachment.contentType};base64,${attachment.fileData}`} alt={attachment.fileName} sx={{ display: "block", maxWidth: "100%", maxHeight: 360, mt: 1, objectFit: "contain" }} />
                                        )}
                                    </Box>
                                ))}
                            </Stack>
                        ) : (
                            <Typography variant="body2" color="text.secondary"><Localized id="bug-report-list-no-attachments">No attachments</Localized></Typography>
                        )}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <CloseButton loading={false} onClick={onClose} />
            </DialogActions>
        </Dialog>
    );
};
